import { Component, ComponentRef, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { Select } from '@ngxs/store';

import { AppLayoutService } from '../../app-layout.service';
import { TokenService } from '@services/token.service';
import { CompaniesService } from '@services/companies.service';
import { CompanyModel } from '@data-layer/models/companies';
import { UserModel } from '@data-layer/models/user';
import { LCLShipmentModel, QuoteModel } from '@data-layer/models/lcl';
import { SelfFilerService } from '@services/self-filer.service';
import { IHeader, IHeaderItem } from '../../interfaces';
import { GenericService } from '@services/generic.service';
import { GoogleAnalyticsReportService } from '@services/google-analytics-report.service';
import { ApplicationModeService } from '@services/application-mode.service';
import { enumApplicationMode } from '@enum/enumApplicationMode';
import { LCLShippingsSelectors } from '@app/modules/lcl-shipping/state';
import { HeaderType } from '@app/modules/app-layout/enums/headerType';
import { enumDetailedInfoPlacementEnum } from '@app/data-layer/enums/lcl';
import { InternalNotesComponent } from '@shared/internal-notes/components';
import { AppService } from '@app/app.service';
import { EntrySummaryRepository, IsfRepository } from '@data-layer/repositories';
import { InternalNote } from '@shared/internal-notes/interfaces';
import { USA_COUNTRY_CODE } from '@constants/constants';
import { ACCESS_TOKEN } from '@constants/setting-keys.const';
import { JwtHelperService } from '@auth0/angular-jwt';
import { TranslateService } from '@ngx-translate/core';
import { OrganizationsService } from '@services/organizations.service';

@Component({
  selector: 'app-content-header',
  templateUrl: './content-header.component.html',
  styleUrls: ['./content-header.component.scss'],
})
export class ContentHeaderComponent implements OnInit, OnDestroy {
  @Select(LCLShippingsSelectors.getSelectedQuote) public quote$: Observable<QuoteModel>;
  @Select(LCLShippingsSelectors.getSelectedShipment) public shipment$: Observable<LCLShipmentModel>;

  public headerTypes: typeof HeaderType = HeaderType;
  public headerData: IHeader = null;
  public internalNote: InternalNote;
  public headerType: HeaderType;
  public smallName: string;
  public user: UserModel;
  public company: CompanyModel;
  public companies: CompanyModel[] = [];
  public isPrinting$: Observable<boolean>;
  public isSupportOrAdmin = false;
  public formatDateTime = 'MM/dd/yyyy';
  public companySearchTerm: string = '';
  public hasMultipleOrganizations: boolean = false;

  public enumDetailedInfoPlacementEnum: typeof enumDetailedInfoPlacementEnum = enumDetailedInfoPlacementEnum;

  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>();
  private companySearch$: Subject<string> = new Subject<string>();

  constructor(
    private appLayoutService: AppLayoutService,
    private tokenService: TokenService,
    private companiesService: CompaniesService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private selfFilerService: SelfFilerService,
    private genericService: GenericService,
    private googleAnalyticsReportService: GoogleAnalyticsReportService,
    private applicationModeService: ApplicationModeService,
    private appService: AppService,
    private entrySummaryRepository: EntrySummaryRepository,
    private isfRepository: IsfRepository,
    private jwtHelperService: JwtHelperService,
    private translateService: TranslateService,
    private organizationsService: OrganizationsService,
  ) {
    this.isPrinting$ = this.genericService.isPrinting$;
  }

  public ngOnInit(): void {
    this.appLayoutService.contentHeader$.pipe(takeUntil(this.destroy$)).subscribe((val) => {
      this.headerData = val;
    });
    this.appLayoutService.internalNotes$.pipe(takeUntil(this.destroy$)).subscribe((internalNote: InternalNote) => {
      this.internalNote = internalNote;
    });

    this.appLayoutService.headerTypeSource$.pipe(takeUntil(this.destroy$)).subscribe((headerType: HeaderType) => {
      this.headerType = headerType;
    });

    this.companiesService.companyChange$.pipe(takeUntil(this.destroy$)).subscribe((companyChanges) => {
      this.company = companyChanges.current;
    });

    this.companiesService.companiesChange$.pipe(takeUntil(this.destroy$)).subscribe((companiesChanges) => {
      this.companies = companiesChanges.current;
      this.sortAlphabeticallyByBusinessName();
    });

    this.user = this.companiesService.getUser();
    this.smallName = !!this.user ? `${this.user.firstName.charAt(0)}${this.user.lastName.charAt(0)}`.toUpperCase() : '';
    this.isSupportOrAdmin = this.companiesService.isSupportOrAdmin();

    this.companySearch$.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe((searchTerm: string) => {
      this.companySearchTerm = searchTerm.trim();
    });

    this.organizationsService.getOrganizationAsync(false, false).pipe(takeUntil(this.destroy$)).subscribe();

    this.organizationsService.organizationsChange$.pipe(takeUntil(this.destroy$)).subscribe(({ current }) => {
      this.hasMultipleOrganizations = current?.length > 1;
    });
  }

  public openInternalNotes(): void {
    const componentSidebarRef: ComponentRef<InternalNotesComponent> = this.appService.openSidebar(InternalNotesComponent, {
      sidebarHeaderText: !!this.internalNote.internalNotes ? 'LABELS.INTERNAL_NOTES_EDIT' : 'LABELS.INTERNAL_NOTES_ADD',
      tooltipText: !!this.internalNote.internalNotes ? 'LABELS.INTERNAL_NOTES_EDIT_DESCRIPTION' : 'LABELS.INTERNAL_NOTES_ADD_DESCRIPTION',
    });
    componentSidebarRef.instance.internalNote = this.internalNote;
    componentSidebarRef.instance.saveInternalNotes.pipe(takeUntil(this.destroy$)).subscribe((data: InternalNote) => {
      (this.internalNote.isIsfPlacement ? this.updateIsfInternalNotes(data) : this.updateEsInternalNotes(data)).subscribe(() => {
        this.appLayoutService.internalNotes$.next({
          ...this.internalNote,
          internalNotes: data.internalNotes,
          internalReferenceNumber: data.internalReferenceNumber,
        });
        this.appLayoutService.internalNotesUpdated$.next();
        this.appService.closeSidebar();
      });
    });
  }

  public updateIsfInternalNotes(data: InternalNote): Observable<void> {
    return this.isfRepository.updateInternalNote(this.internalNote.id, data).pipe(takeUntil(this.destroy$));
  }

  public updateEsInternalNotes(data: InternalNote): Observable<void> {
    return this.entrySummaryRepository.updateInternalNote(this.internalNote.id, data).pipe(takeUntil(this.destroy$));
  }

  public openRecalculate(): void {
    this.appLayoutService.recalculateBeforeEdit$.next(true);
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public get isShowLCLHeader(): boolean {
    return ![
      this.headerTypes.Basic,
      this.headerTypes.ESNotes,
      this.headerTypes.ISFNotes,
      this.headerTypes.ISFList,
      this.headerTypes.ESList,
      this.headerTypes.MarineInsuranceView,
    ].includes(this.headerType);
  }

  public get isShowBasicHeader(): boolean {
    return [
      this.headerTypes.Basic,
      this.headerTypes.ESNotes,
      this.headerTypes.ISFNotes,
      this.headerTypes.ISFList,
      this.headerTypes.ESList,
      this.headerTypes.MarineInsuranceView,
    ].includes(this.headerType);
  }

  public get isShowInternalNotes(): boolean {
    return [this.headerTypes.ESNotes, this.headerTypes.ISFNotes].includes(this.headerType);
  }

  public get isShowRecalculate(): boolean {
    return [this.headerTypes.MarineInsuranceView].includes(this.headerType);
  }

  public queryParams(segment: IHeaderItem) {
    if (segment.resetQueryParams) {
      return null;
    }

    if (!!segment.queryParams) {
      return segment.queryParams;
    }

    return this.activatedRoute && this.activatedRoute.snapshot ? this.activatedRoute.snapshot.queryParams : undefined;
  }

  public get isShowOnBoarding() {
    return this.selfFilerService.isShowOnBoarding;
  }

  // Route-aware HTS Classification History toggle icon (shown only on BINA Catalog / its history
  // page). Icon + tooltip reflect the destination: catalog → history, history → back to catalog.
  public get isHtsCatalogArea(): boolean {
    return this.htsPath === '/bina-catalog' || this.htsPath === '/bina-catalog/history';
  }

  public get isHtsHistoryPage(): boolean {
    return this.htsPath === '/bina-catalog/history';
  }

  public get htsToggleIcon(): string {
    return this.isHtsHistoryPage ? 'assets/icons/hts-catalog.svg' : 'assets/icons/hts-history.svg';
  }

  public get htsToggleTooltip(): string {
    return this.isHtsHistoryPage ? 'BINA_CATALOG.HTS_HISTORY.BACK_TOOLTIP' : 'BINA_CATALOG.HTS_HISTORY.VIEW_TOOLTIP';
  }

  public onToggleHtsHistory(): void {
    this.router.navigate(this.isHtsHistoryPage ? ['/bina-catalog'] : ['/bina-catalog', 'history']);
  }

  private get htsPath(): string {
    return this.router.url.split('?')[0].split('#')[0];
  }

  public selectCompany(event: Event, company: CompanyModel, callback?: () => void) {
    // TODO check it! commented in #4612,
    /*event.stopPropagation();
    event.preventDefault();*/
    this.loadUserBetaFlags(company);
    this.companiesService
      .getCompanyAsync(true, true, company?.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.companiesService.setDefaultCompany(company, callback);
      });
  }

  public redirectToCompanyView(company: CompanyModel): void {
    this.selectCompany(null, company, () => {
      this.router.navigate(['/companies', 'account-settings', 'company-view', company.id]);
    });
  }

  public logout(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.tokenService.eraseToken(true, false);
    this.googleAnalyticsReportService.logout();
  }

  public get isActiveSupportMode() {
    return this.applicationModeService.activeApplicationMode === enumApplicationMode.SUPPORT;
  }

  public get isActiveUserMode() {
    return this.applicationModeService.activeApplicationMode === enumApplicationMode.USER;
  }

  public get isAllowSwitchMode(): boolean {
    return this.companiesService.isSupportOrAdmin() && this.companiesService.isUser();
  }

  public switchOrganization(): void {
    this.router.navigate(['/auth', 'login'], { queryParams: { switchOrg: true } });
  }

  public switchActiveApplicationMode() {
    this.loadUserBetaFlags();
    this.applicationModeService.switchActiveApplicationMode();
  }

  public isCurrentCompany(companyId: number): boolean {
    return companyId === this.company?.id;
  }

  public getCompanyLabel(company: CompanyModel, index: number): string {
    return company?.businessName ? company.businessName : `${this.translateService.instant('LABELS.EMPTY_IOR')} #${index + 1}`;
  }

  public get isShowCompanySearch(): boolean {
    return this.companies?.length > 5 && this.isActiveUserMode;
  }

  public get filteredCompanies(): CompanyModel[] {
    if (!this.companySearchTerm) {
      return this.companies;
    }
    const searchLower = this.companySearchTerm.toLowerCase();
    return this.companies.filter((company) => company.businessName?.toLowerCase().includes(searchLower));
  }

  public onCompanySearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.companySearch$.next(value);
  }

  public resetCompanySearch(): void {
    this.companySearchTerm = '';
  }

  public onDropdownOpenChange(isOpen: boolean): void {
    if (!isOpen) {
      this.resetCompanySearch();
    }
  }

  public get isShowSelfFiller(): boolean {
    const company: CompanyModel = this.companiesService.getCompany();

    return !!company && this.isShowOnBoarding && company?.address?.country?.code === USA_COUNTRY_CODE;
  }

  private loadUserBetaFlags(company?: CompanyModel): void {
    this.companiesService
      .getUserBetaFlags({
        userExternalId: this.userExternalId,
        companyId: !!company ? company.id : this.company.id,
        organizationId: !!company ? company.organizationId : this.company.organizationId,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe((betaFlags) => {
        if (!!betaFlags) {
          this.companiesService.setUserBetaFlags(betaFlags);
        }
      });
  }

  private get userExternalId(): string {
    return this.jwtHelperService.decodeToken(localStorage.getItem(ACCESS_TOKEN))?.user_external_id || null;
  }

  private sortAlphabeticallyByBusinessName() {
    this.companies.sort((a, b) => {
      if (a.businessName < b.businessName) {
        return -1;
      }
      if (a.businessName > b.businessName) {
        return 1;
      }
      return 0;
    });
  }
}
